import React, { useState, useRef, useEffect } from 'react';
import { ZAZZLE_CONFIG, ZAZZLE_PRODUCTS, ZazzleProductType } from '../config/zazzle';
import { buildZazzleUrl } from '../utils/zazzle';

type ImageMeta = {
  filename: string;
  tags: string[];
  url?: string;
};

export interface ImageModalProps {
  image: ImageMeta;
  onClose: () => void;
}

// Direct GCS URL for Zazzle (bucket must be public or Zazzle must have access)
const GCS_BUCKET_URL = 'https://storage.googleapis.com/pepe-images';

const ImageModal: React.FC<ImageModalProps> = ({ image, onClose }) => {
  const [merchDropdownOpen, setMerchDropdownOpen] = useState(false);
  const [merchLoading, setMerchLoading] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (dropdownRef.current && target instanceof Node && !dropdownRef.current.contains(target)) {
        setMerchDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!image) return null;

  const handleCreateMerch = (productType: ZazzleProductType) => {
    setMerchLoading(productType);
    try {
      const imageUrl = `${GCS_BUCKET_URL}/${encodeURIComponent(image.filename)}`;
      const zazzleUrl = buildZazzleUrl(imageUrl, productType, image.filename);
      window.open(zazzleUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setMerchLoading(null);
      setMerchDropdownOpen(false);
    }
  };

  const handleDownload = async () => {
    if (!image.url) return;
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = image.filename || 'pepe-image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download image:', err);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#1e1e1e',
          padding: 20,
          borderRadius: 12,
          maxWidth: '90%',
          maxHeight: '90%',
          overflow: 'auto',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
          {ZAZZLE_CONFIG.enabled && image.url && (
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setMerchDropdownOpen(!merchDropdownOpen)}
                style={{
                  background: '#4a3a8a',
                  color: '#e0e0e0',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                Create Merch
                <span style={{ fontSize: 10, marginLeft: 2 }}>{merchDropdownOpen ? '\u25B2' : '\u25BC'}</span>
              </button>
              {merchDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    background: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    zIndex: 10,
                    minWidth: 160,
                    overflow: 'hidden',
                  }}
                >
                  {ZAZZLE_PRODUCTS.map((product) => (
                    <button
                      key={product.type}
                      onClick={() => handleCreateMerch(product.type)}
                      disabled={merchLoading !== null}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'transparent',
                        color: '#e0e0e0',
                        border: 'none',
                        borderBottom: '1px solid #333',
                        cursor: merchLoading !== null ? 'wait' : 'pointer',
                        textAlign: 'left',
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        opacity: merchLoading !== null ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (merchLoading === null) e.currentTarget.style.background = '#3a3a3a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span>{product.emoji}</span>
                      <span>{product.label}</span>
                      {merchLoading === product.type && (
                        <span style={{ marginLeft: 'auto', fontSize: 12 }}>...</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {image.url && (
            <button
              onClick={handleDownload}
              style={{
                background: '#2d5a27',
                color: '#e0e0e0',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Download
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: '#333',
              color: '#e0e0e0',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Close
          </button>
        </div>
        {image.url ? (
          <img src={image.url} alt="Pepe image" style={{ maxWidth: '100%', height: 'auto', display: 'block', marginBottom: 16, borderRadius: 8 }} />
        ) : (
          <div style={{ width: '100%', paddingTop: '56%', background: '#2a2a2a', marginBottom: 16, borderRadius: 8 }} />
        )}
        <div style={{ marginTop: 12 }}>
          <strong style={{ color: '#e0e0e0' }}>Tags:</strong>
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {image.tags && image.tags.length ? image.tags.map(t => (
              <span key={t} style={{ display: 'inline-block', padding: '6px 12px', background: '#333', color: '#e0e0e0', borderRadius: 6, fontSize: 14 }}>
                {t}
              </span>
            )) : <span style={{ color: '#666' }}>No tags</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;